import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PermissionGuard/PrivateRoute';
import RouteGuard from './components/PermissionGuard/RouteGuard';
import Layout from './components/Layout/Layout';
import LoadingFallback from './components/LoadingFallback/LoadingFallback';
import DefaultRoute from './components/DefaultRoute/DefaultRoute';
import Login from './pages/Login/Login';
import { flatModulePages } from './config/moduleMenus';
import { hrPageRoutes } from './pages/HR/hrRoutes';
import { attendancePageRoutes } from './pages/Attendance/attendanceRoutes';
import { adminPageRoutes } from './pages/Admin/adminRoutes';
import { expensePageRoutes } from './pages/Expense/expenseRoutes';
import { materialsPageRoutes } from './pages/Materials/materialsRoutes';
import { planPageRoutes } from './pages/Plan/planRoutes';
import { salaryPageRoutes } from './pages/Salary/salaryRoutes';
import { deptLedgerPageRoutes } from './pages/DeptLedger/deptLedgerRoutes';
import { portalPageRoutes } from './pages/Portal/portalRoutes';

const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Portal = lazy(() => import('./pages/Portal/Portal'));
const ApprovalCenter = lazy(() => import('./pages/ApprovalCenter/ApprovalCenter'));
const InboxCenter = lazy(() => import('./pages/Inbox/InboxCenter'));
const SystemUsers = lazy(() => import('./pages/System/Users/Users'));
const SystemRoles = lazy(() => import('./pages/System/Roles/Roles'));
const SystemOperationLogs = lazy(() => import('./pages/System/OperationLogs/OperationLogs'));
const SystemWorkflows = lazy(() => import('./pages/System/Workflow/WorkflowManage'));
const SystemOnboardingFormConfig = lazy(() => import('./pages/System/OnboardingFormConfig/OnboardingFormConfig'));
const SystemOpsBoard = lazy(() => import('./pages/System/OpsBoard/OpsBoard'));
const WorkflowDesigner = lazy(() => import('./pages/System/Workflow/WorkflowDesigner'));
const PlaceholderPage = lazy(() => import('./pages/Placeholder/PlaceholderPage'));
const AnnouncementView = lazy(() => import('./pages/Admin/Announcement/AnnouncementView'));

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path="/" element={<RouteGuard><DefaultRoute /></RouteGuard>} />
                      <Route path="/dashboard" element={<RouteGuard><Dashboard /></RouteGuard>} />
                      <Route path="/portal" element={<RouteGuard><Portal /></RouteGuard>} />
                      {portalPageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      <Route path="/approval-center" element={<RouteGuard><ApprovalCenter /></RouteGuard>} />
                      <Route path="/messages" element={<RouteGuard><InboxCenter /></RouteGuard>} />
                      <Route path="/announcements/:id" element={<RouteGuard><AnnouncementView /></RouteGuard>} />
                      <Route path="/system/users" element={<RouteGuard><SystemUsers /></RouteGuard>} />
                      <Route path="/system/roles" element={<RouteGuard><SystemRoles /></RouteGuard>} />
                      <Route path="/system/operation-logs" element={<RouteGuard><SystemOperationLogs /></RouteGuard>} />
                      <Route path="/system/workflows" element={<RouteGuard><SystemWorkflows /></RouteGuard>} />
                      <Route path="/system/onboarding-form-config" element={<RouteGuard><SystemOnboardingFormConfig /></RouteGuard>} />
                      <Route path="/system/ops-board" element={<RouteGuard><SystemOpsBoard /></RouteGuard>} />
                      <Route path="/system/workflows/design/:bizType" element={<RouteGuard><WorkflowDesigner /></RouteGuard>} />
                      {hrPageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      {attendancePageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      {adminPageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      {expensePageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      {materialsPageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      {planPageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      {salaryPageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      {deptLedgerPageRoutes.map(({ path, component: PageComponent }) => (
                        <Route key={path} path={path} element={<RouteGuard><PageComponent /></RouteGuard>} />
                      ))}
                      {flatModulePages.filter((page) => {
                        const implementedExpensePaths = expensePageRoutes.map((r) => r.path);
                        const implementedMaterialsPaths = materialsPageRoutes.map((r) => r.path);
                        return !page.path.startsWith('/hr/')
                          && !page.path.startsWith('/attendance/')
                          && !page.path.startsWith('/admin/')
                          && !page.path.startsWith('/dept-ledger/')
                          && !implementedExpensePaths.includes(page.path)
                          && !implementedMaterialsPaths.includes(page.path)
                          && !page.path.startsWith('/plan/')
                          && !page.path.startsWith('/salary/');
                      }).map((page) => (
                        <Route
                          key={page.path}
                          path={page.path}
                          element={<RouteGuard><PlaceholderPage /></RouteGuard>}
                        />
                      ))}
                    </Routes>
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
